import type { FieldMask } from './fieldMask'
import {
  deriveNumberSymbols,
  deriveScaledNumberSymbols,
  getNumberFormatter,
  isSignificantChar,
  maskNumber,
  type NumberScale,
  parseLocaleNumber,
  sanitizePartialNumber,
} from './numberParse'

export type NumberMaskConfig = {
  /** BCP-47 tag. Supplied by the caller — this package has no locale of its own. */
  locale: string
  format?: 'decimal' | 'currency'
  /** Only meaningful when `format` is `'currency'`. */
  currency?: string
  minFractionDigits?: number
  maxFractionDigits?: number
  allowNegative?: boolean
  /**
   * Off for years, quantity counts and multipliers, where a thousands separator is simply wrong —
   * a year would otherwise read `2,026`.
   */
  grouping?: boolean
  /**
   * Denomination of the stored value, written into the mask as the locale's compact abbreviation —
   * `$1M` in en-CA, `1 M$` in fr-CA for `'million'` — without scaling the number: the field reads
   * and writes `1` to mean one million.
   */
  scale?: NumberScale
}

/**
 * A {@link FieldMask} that reads and writes numbers the way `locale` writes them.
 *
 * Everything numeric lives here so that `MaskedField` stays ignorant of it, and the numeric
 * primitives in `numberParse` stay usable on their own.
 */
export function numberMask(config: NumberMaskConfig): FieldMask<number> {
  const {
    locale,
    format = 'decimal',
    currency = 'CAD',
    minFractionDigits,
    maxFractionDigits,
    allowNegative = false,
    grouping = true,
    scale,
  } = config

  const fractionOptions: Intl.NumberFormatOptions = {
    minimumFractionDigits: minFractionDigits,
    maximumFractionDigits: maxFractionDigits,
  }
  const displayOptions: Intl.NumberFormatOptions =
    format === 'currency'
      ? {
          style: 'currency',
          currency,
          currencyDisplay: 'narrowSymbol',
          useGrouping: grouping,
          ...fractionOptions,
        }
      : { style: 'decimal', useGrouping: grouping, ...fractionOptions }

  const symbols = scale
    ? deriveScaledNumberSymbols(locale, displayOptions, scale)
    : deriveNumberSymbols(locale, displayOptions)

  // Ask the formatter what precision it actually settled on rather than trusting the config: a
  // currency style falls back to the currency's own places (two, for CAD) when the caller names
  // none, so these are the digits the field really shows and therefore the digits it should accept.
  const resolved = getNumberFormatter(locale, displayOptions).resolvedOptions()
  const editFormatter = getNumberFormatter(locale, {
    style: 'decimal',
    minimumFractionDigits: resolved.minimumFractionDigits,
    maximumFractionDigits: resolved.maximumFractionDigits,
    useGrouping: false,
  })

  // Hand over the number without grouping or symbols, but still carrying the precision the idle
  // display showed, or the field visibly loses its decimals the moment it is clicked.
  const fromValue = (value: number) => editFormatter.format(value).split(symbols.decimal).join('.')

  return {
    fromText: (text) =>
      sanitizePartialNumber(text, symbols, allowNegative, resolved.maximumFractionDigits),
    fromValue,
    render: (draft) => maskNumber(draft, locale, symbols, grouping),
    // Intl has no notion of an unscaled number wearing a compact abbreviation, so a scaled field's
    // idle display is composed the way render composes it rather than handed to Intl.
    display: (value) =>
      scale
        ? maskNumber(fromValue(value), locale, symbols, grouping)
        : getNumberFormatter(locale, displayOptions).format(value),
    parse: (draft) => parseLocaleNumber(draft, symbols),
    isSignificant: (char) => isSignificantChar(char, symbols),
    // The minus key is missing from the Android numeric keypad, and a whole-number field has no
    // use for the decimal one.
    inputMode: resolved.maximumFractionDigits === 0 && !allowNegative ? 'numeric' : 'decimal',
  }
}
